/* eslint-disable @typescript-eslint/no-unused-vars */
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { Autocomplete, Input, ScrollableTabPanel, UploadFile } from '../../../components';
import AcceptanceTermsType from '../../../entities/acceptance-term-type';
import AcceptanceTerm from '../../../entities/acceptance-term.entity';
import { TermFormShape, schema, TermForm } from '../../../validations/term.validation';

interface TermPanelProps {
  term: AcceptanceTerm;
  visible: boolean;
  file: File | null;
  loading: boolean;
  handleUpload: (file: File | null) => void;
  onClickClose: () => void;
  onSubmit: (value: AcceptanceTerm) => void;
}

const typeOptions = [
  AcceptanceTermsType.AppPrivacyTerms,
  AcceptanceTermsType.AppUseTerms,
  AcceptanceTermsType.Laundry,
  AcceptanceTermsType.Cleaning,
  AcceptanceTermsType.Maid,
  AcceptanceTermsType.Market,
  AcceptanceTermsType.Furniture,
];

const typeOptionsLabelMapper: Record<AcceptanceTermsType, string> = {
  [AcceptanceTermsType.AppPrivacyTerms]: 'Termos de privacidade',
  [AcceptanceTermsType.AppUseTerms]: 'Termos de uso',
  [AcceptanceTermsType.Laundry]: 'Termos da lavanderia',
  [AcceptanceTermsType.Maid]: 'Termos da camareira',
  [AcceptanceTermsType.Cleaning]: 'Termos da limpeza',
  [AcceptanceTermsType.Market]: 'Termos de mercado',
  [AcceptanceTermsType.Furniture]: 'Termos de móveis',
};

export const TermPanel = ({
  onClickClose,
  onSubmit,
  term,
  visible,
  file,
  loading,
  handleUpload,
}: TermPanelProps): JSX.Element => {
  const {
    control,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    register,
    handleSubmit: handleValidation,
    reset,
  } = useForm<TermForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const type = useWatch({ control, name: 'termType' }) as AcceptanceTermsType;

  useEffect(() => {
    reset({
      termType: term.type,
      version: term.version?.toString(),
    });
  }, [term, reset]);

  const handleClickSubmit = async () => {
    const values = getValues();

    return onSubmit({ ...term, ...values, version: Number(values.version), type: values.termType! });
  };

  return (
    <ScrollableTabPanel
      title="Dados do termo"
      visible={visible}
      loading={loading}
      disabled={Boolean(term.id)}
      onClickClose={onClickClose}
      onClickSubmit={handleValidation(handleClickSubmit)}
      mustConfirm
      mustConfirmContent="Deseja continuar a operação? Ao fazer isso todos os usuário estarão sujeitos ao aceite desta nova versão."
    >
      <Autocomplete
        label="Selecione o tipo"
        placeholder="Ex: Termos de uso"
        options={typeOptions}
        getOptionLabel={(opt) => typeOptionsLabelMapper[opt]}
        value={type ?? null}
        disabled={Boolean(term.id)}
        onChange={(e, value) => setValue('termType', value)}
        error={Boolean(errors.termType)}
        helperText={errors.termType?.message || ' '}
      />
      <Input
        label="Versão"
        placeholder="Ex: 1"
        mask={{ mask: '0000' }}
        disabled={Boolean(term.id)}
        validationRegister={register('version')}
        error={Boolean(errors.version)}
        helperText={errors.version?.message || ' '}
      />
      <UploadFile
        file={file ?? term.url}
        readonly={Boolean(term.id)}
        errorMessage={(errors.url?.message as unknown as string) ?? ' '}
        error={Boolean(errors.url)}
        onUpload={(value) => {
          handleUpload(value);
          setValue('url', value);
        }}
      />
    </ScrollableTabPanel>
  );
};

export default TermPanel;
